import Dexie, { type Table } from 'dexie';
import type { AttemptInput, AttemptRejection, AttemptResult, SessionResponse } from '@lk/shared';

/**
 * Локальне сховище — фундамент надійності застосунку.
 *
 * Правило: відповідь дитини спершу лягає СЮДИ, і лише потім показується фідбек.
 * Мережа може зникнути, вкладка — закритися, телефон — розрядитися;
 * усе, що дитина встигла відповісти, лишиться в IndexedDB і поїде на сервер,
 * щойно зʼявиться звʼязок.
 */

export type AttemptStatus =
  /** Лежить у черзі, чекає відправки. */
  | 'pending'
  /** Прямо зараз у польоті. Якщо вкладку вбити — на старті повернеться в pending. */
  | 'syncing'
  /** Сервер підтвердив прийом. */
  | 'synced'
  /**
   * Сервер відмовив. Стан ТИМЧАСОВИЙ і оборотний: самі ми більше не ретраїмо,
   * але рядок повертається в `pending`, щойно за пристрій сідає інша дитина
   * або хтось натискає «спробувати ще раз». Односторонньої дороги звідси немає —
   * інакше відповіді дитини згорали б назавжди через чужу помилку.
   */
  | 'rejected';

export interface QueuedAttempt {
  /** = AttemptInput.id, UUIDv7 з клієнта. Первинний ключ і ключ ідемпотентності. */
  id: string;
  learnerId: string;
  sessionId: string;
  itemId: string;
  /** Точне тіло запиту за контрактом — щоб ретрай слав байт-у-байт те саме. */
  payload: AttemptInput;
  status: AttemptStatus;
  /** Скільки разів уже намагалися відправити. Керує експоненційною затримкою. */
  tries: number;
  /**
   * Скільки разів сервер сказав саме «поверни цю спробу пізніше».
   *
   * Живе окремо від `tries` навмисно: `tries` росте і від мережевих невдач,
   * тож стеля оборотних вироків на ньому спалювала б чесну відповідь після
   * кількох хвилин недоступного сервера. Поле не індексоване — версію Dexie
   * піднімати не треба, у старих рядках воно просто `undefined`.
   */
  verdicts?: number;
  /** Не раніше цього моменту (epoch ms) робити наступну спробу. */
  nextAttemptAt: number;
  lastError: string | null;
  createdAt: number;
  syncedAt: number | null;
  /** Авторитетний результат від сервера. Доки null — питання «ще не перевірене». */
  result: AttemptResult | null;
  /**
   * Чому сервер не зарахував саме цю відповідь. `null` — відмови не було.
   *
   * Без цього поля рядок розрізняв лише дві речі («є результат» / «немає»),
   * і незарахована спроба виглядала для дитини так само, як та, що ще
   * чекає перевірки. Екран підсумку через це обіцяв бали за роботу, за яку
   * їх не буде: «результат і бали додадуться сюди автоматично».
   *
   * Поле не індексоване, тож версію Dexie піднімати не треба: у старих
   * рядках воно просто `undefined` і читається як «причини не знаємо».
   */
  rejection?: AttemptRejection | null;
}

export interface StoredSession {
  sessionId: string;
  learnerId: string;
  /** Уся сесія цілком: усі питання видані сервером на старті. */
  payload: SessionResponse;
  /** Індекс питання, на якому дитина зупинилася. Переживає F5. */
  cursor: number;
  startedAt: number;
  finishedAt: number | null;
}

export interface MetaRow {
  key: string;
  value: unknown;
}

export interface CacheRow {
  key: string;
  value: unknown;
  updatedAt: number;
}

export class LearnKidsDb extends Dexie {
  attempts!: Table<QueuedAttempt, string>;
  sessions!: Table<StoredSession, string>;
  meta!: Table<MetaRow, string>;
  cache!: Table<CacheRow, string>;

  constructor(name = 'learnkids') {
    super(name);
    this.version(1).stores({
      // Складений індекс [status+nextAttemptAt] — саме за ним черга вибирає,
      // що вже дозріло для наступної спроби.
      attempts: 'id, status, sessionId, learnerId, createdAt, [status+nextAttemptAt]',
      sessions: 'sessionId, learnerId, startedAt, finishedAt',
      meta: 'key',
      cache: 'key',
    });
    // Планшет один на двох дітей і батька, а база Dexie — одна на пристрій.
    // Тому вибірка «що слати далі» мусить починатися з learnerId, інакше
    // синхронізатор фізично здатен віддати чужі відповіді під сесією того,
    // хто зайшов зараз. Індекс [learnerId+status+nextAttemptAt] робить
    // таку вибірку єдиною можливою.
    this.version(2).stores({
      attempts:
        'id, status, sessionId, learnerId, createdAt, [status+nextAttemptAt], [learnerId+status+nextAttemptAt]',
    });
  }
}

let singleton: LearnKidsDb | null = null;

/** Спільний екземпляр для застосунку. Тести створюють власні через `new LearnKidsDb(name)`. */
export function getDb(): LearnKidsDb {
  singleton ??= new LearnKidsDb();
  return singleton;
}

/** Тільки для тестів: підмінити активну базу. */
export function __setDbForTests(db: LearnKidsDb | null): void {
  singleton = db;
}
